import {BLOOD_SPLATTER} from '../../configs/bloodSplatter';

const randomRange = (rng, {min, max}) => min + rng() * (max - min);
const lerp = ({min, max}, share) => min + (max - min) * share;

/**
 * Брызги крови от попадания: капли разлетаются от точки удара, больше — по
 * ходу удара, летят дугой и падают; там, где упали самые крупные, остаются
 * пятна (см. configs/bloodSplatter).
 *
 * @param {Object} params
 * @param {{x: number, y: number}} params.at — точка попадания
 * @param {number} params.direction — направление удара, радианы
 * @param {number} params.hexSize — размер ячейки
 * @param {number} params.intensity — сила 0..1 (resolveBloodIntensity)
 * @param {Function} [params.rng]
 * @returns {{droplets: Array<{startX, startY, endX, endY, lift, size, delayMs, durationMs}>,
 *   stains: Array<{x, y, size, rotation, alpha}>}}
 */
export const buildBloodSplatter = ({at, direction, hexSize, intensity, rng = Math.random} = {}) => {
  if (!at || !(hexSize > 0) || !(intensity > 0)) {
    return {droplets: [], stains: []};
  }
  const share = Math.min(1, intensity);
  const {droplets: dropConfig, stains: stainConfig} = BLOOD_SPLATTER;
  const count = Math.round(lerp(dropConfig.count, share));
  const droplets = [];
  for (let index = 0; index < count; index += 1) {
    const isBackward = rng() < dropConfig.backwardShare;
    // Конус делится на секторы — капли не слипаются в одну струю
    const sector = (index + rng()) / count - 0.5;
    const angle = direction + (isBackward ? Math.PI : 0) + sector * dropConfig.spreadRad * (isBackward ? 0.6 : 1);
    // Сильный удар разбрасывает дальше
    const distance = hexSize * randomRange(rng, dropConfig.distanceFactor) * (0.75 + 0.25 * share) * (isBackward ? 0.5 : 1);
    droplets.push({
      startX: at.x,
      startY: at.y,
      endX: at.x + Math.cos(angle) * distance,
      endY: at.y + Math.sin(angle) * distance,
      lift: hexSize * randomRange(rng, dropConfig.liftFactor),
      size: hexSize * randomRange(rng, dropConfig.sizeFactor),
      delayMs: Math.round(rng() * dropConfig.maxDelayMs),
      durationMs: Math.round(randomRange(rng, dropConfig.durationMs)),
    });
  }

  // Пятна — под самыми крупными каплями, там, где они упали
  const stainCount = Math.min(droplets.length, Math.round(lerp(stainConfig.count, share)));
  const stains = [...droplets]
    .sort((a, b) => b.size - a.size)
    .slice(0, stainCount)
    .map((droplet) => ({
      x: droplet.endX,
      y: droplet.endY,
      size: hexSize * randomRange(rng, stainConfig.sizeFactor) * (0.7 + 0.3 * share),
      rotation: rng() * Math.PI * 2,
      alpha: stainConfig.alpha,
      // Пятно появляется, когда капля долетела
      appearAtMs: droplet.delayMs + droplet.durationMs,
    }));

  return {droplets, stains};
};

/**
 * Положение капли на дуге по ходу полета 0..1: по прямой к точке падения и с
 * подъемом посередине (парабола)
 */
export const resolveBloodDropletPosition = (droplet, progress) => {
  const t = Math.min(1, Math.max(0, progress));
  return {
    x: droplet.startX + (droplet.endX - droplet.startX) * t,
    y: droplet.startY + (droplet.endY - droplet.startY) * t - droplet.lift * 4 * t * (1 - t),
  };
};
